/**
 * Builder mode: the app running inside the dashboard's chart builder.
 *
 * Add chart and Edit chart host a second copy of Explore in a same-origin
 * frame (`features/dashboard/chart-builder.ts`), opened on the chart's recipe
 * with `?embed=builder`. Its own module instances are its own state, so
 * nothing the builder does reaches the Explore the page is showing, and
 * nothing in Explore reaches the builder.
 *
 * What a frame must not do, it checks here: render the app's chrome, write the
 * address bar or the tab's working draft (the frame shares the page's
 * `sessionStorage`), or record Home's recent views.
 *
 * Read once, at load: the frame's URL is its launch recipe and never changes.
 */

function launchParams(): URLSearchParams {
  try {
    return new URLSearchParams(window.location.search);
  } catch {
    return new URLSearchParams();
  }
}

const params = typeof window === 'undefined' ? new URLSearchParams() : launchParams();

/** True when this app is the chart builder's frame. */
export const BUILDER_EMBED = params.get('embed') === 'builder';

/** The dashboard's windows, for the builder's Data source step. */
export interface BuilderWindows {
  /** The Live window's preset ("10m"): a switch to Live opens on it. */
  livePreset: string | null;
  /** The dashboard's Time: a switch to EOS goes back to it. */
  historyStart: string | null;
  historyEnd: string | null;
}

export const builderWindows: BuilderWindows = {
  livePreset: params.get('blw'),
  historyStart: params.get('bhs'),
  historyEnd: params.get('bhe'),
};

/** The URL params that start the app in builder mode - for the host page. */
export function builderLaunchParams(windows: BuilderWindows): URLSearchParams {
  const out = new URLSearchParams({ embed: 'builder' });
  if (windows.livePreset) out.set('blw', windows.livePreset);
  if (windows.historyStart && windows.historyEnd) {
    out.set('bhs', windows.historyStart);
    out.set('bhe', windows.historyEnd);
  }
  return out;
}
